import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, ArrowRight, Calendar, MapPin, Euro, Users, Plus, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyChild = () => ({ first_name: '', last_name: '', dob: '', gender: '' });

const PublicActivityRegister = () => {
    const { activityId } = useParams();
    const navigate = useNavigate();
    const [activity, setActivity] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        guardian_first_name: '', guardian_last_name: '', guardian_email: '', guardian_password: '',
        guardian_mobile: '', guardian_address: '',
        join_waiting_list: false,
    });
    const [children, setChildren] = useState([emptyChild()]);

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API_URL}/api/public/activities/${activityId}`);
                setActivity(res.data);
            } catch (err) {
                toast.error(err.response?.data?.detail || 'Activity not found');
                navigate('/public/activities');
            } finally { setLoading(false); }
        })();
    }, [activityId, navigate]);

    const updateChild = (i, patch) => {
        setChildren(prev => prev.map((c, idx) => idx === i ? { ...c, ...patch } : c));
    };
    const addChild = () => setChildren(prev => [...prev, emptyChild()]);
    const removeChild = (i) => setChildren(prev => prev.filter((_, idx) => idx !== i));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (form.guardian_password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
        for (let i = 0; i < children.length; i++) {
            const c = children[i];
            if (!c.first_name.trim() || !c.last_name.trim()) { toast.error(`Child #${i + 1}: please enter a name`); return; }
            if (!c.dob) { toast.error(`Child #${i + 1}: please select date of birth`); return; }
            if (!c.gender) { toast.error(`Child #${i + 1}: please select gender`); return; }
        }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                students: children.map(c => ({
                    first_name: c.first_name.trim(),
                    last_name: c.last_name.trim(),
                    dob: c.dob,
                    gender: c.gender,
                })),
            };
            const res = await axios.post(
                `${API_URL}/api/public/activities/${activityId}/register`,
                payload,
                { withCredentials: true }
            );
            toast.success('Redirecting to secure payment\u2026');
            window.location.href = res.data.stripe_url;
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Registration failed. Please try again.');
        } finally { setSubmitting(false); }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-8 w-8 animate-spin text-[#064E3B]" /></div>;
    }
    if (!activity) return null;

    const cost = Number(activity.cost || 0);
    const deposit = Number(activity.deposit || 0);
    const perChildDue = deposit > 0 && deposit < cost ? deposit : cost;
    const dueLabel = deposit > 0 && deposit < cost ? 'Deposit' : 'Full payment';
    const n = children.length;
    const totalDue = perChildDue * n;

    return (
        <div className="min-h-screen bg-[#FAF9F6] py-10" data-testid="public-register-page">
            <div className="max-w-3xl mx-auto px-6">
                <Link to="/public/activities" className="inline-flex items-center text-sm text-[#064E3B] hover:underline mb-4">
                    <ArrowLeft className="h-4 w-4 mr-1" /> Back to all activities
                </Link>

                {/* Activity summary */}
                <Card className="card-surface mb-6 overflow-hidden">
                    {activity.image_url ? (
                        <div className="w-full h-48 bg-[#F3F4F6] overflow-hidden">
                            <img
                                src={activity.image_url}
                                alt={activity.name}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                            />
                        </div>
                    ) : null}
                    <CardContent className="p-6 space-y-3">
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">{activity.name}</h1>
                        {activity.description && <p className="text-sm text-[#4B5563]">{activity.description}</p>}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-[#4B5563]">
                            <div className="flex items-start gap-1.5"><Calendar className="h-3.5 w-3.5 mt-0.5 text-[#064E3B]" /><span className="tabular-nums">{activity.start_date} &rarr; {activity.end_date}</span></div>
                            {activity.location && <div className="flex items-start gap-1.5"><MapPin className="h-3.5 w-3.5 mt-0.5 text-[#064E3B]" /><span>{activity.location}</span></div>}
                            <div className="flex items-start gap-1.5"><Euro className="h-3.5 w-3.5 mt-0.5 text-[#064E3B]" /><span className="font-semibold">€{cost.toFixed(2)} per child</span></div>
                            {(activity.min_age != null || activity.max_age != null) && <div className="flex items-start gap-1.5"><Users className="h-3.5 w-3.5 mt-0.5 text-[#064E3B]" /><span>Ages {activity.min_age ?? '?'}&ndash;{activity.max_age ?? '?'}</span></div>}
                        </div>
                        <div className="flex flex-wrap gap-2 pt-1">
                            <Badge variant="outline">Closes {activity.closing_date}</Badge>
                            {activity.gender && activity.gender !== 'both' && <Badge variant="outline" className="capitalize">{activity.gender} only</Badge>}
                            {deposit > 0 && deposit < cost && <Badge className="bg-[#F59E0B] text-white">Deposit option: €{deposit.toFixed(2)} per child</Badge>}
                        </div>
                    </CardContent>
                </Card>

                <Card className="card-surface">
                    <CardContent className="p-6">
                        <h2 className="text-xl font-semibold text-[#1F2937] font-['Outfit'] mb-1">Create your account &amp; register</h2>
                        <p className="text-sm text-[#4B5563] mb-6">You can register one or more children below. You&rsquo;ll be redirected to a secure Stripe payment page after this step.</p>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <h3 className="text-sm font-semibold text-[#064E3B] mb-2">Guardian details</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div className="space-y-1.5"><Label>First name *</Label><Input value={form.guardian_first_name} onChange={(e) => setForm({ ...form, guardian_first_name: e.target.value })} required data-testid="guardian-first-name" /></div>
                                    <div className="space-y-1.5"><Label>Last name *</Label><Input value={form.guardian_last_name} onChange={(e) => setForm({ ...form, guardian_last_name: e.target.value })} required data-testid="guardian-last-name" /></div>
                                    <div className="space-y-1.5"><Label>Email *</Label><Input type="email" value={form.guardian_email} onChange={(e) => setForm({ ...form, guardian_email: e.target.value.toLowerCase() })} required data-testid="guardian-email" /></div>
                                    <div className="space-y-1.5"><Label>Mobile *</Label><Input value={form.guardian_mobile} onChange={(e) => setForm({ ...form, guardian_mobile: e.target.value })} required data-testid="guardian-mobile" /></div>
                                    <div className="space-y-1.5 md:col-span-2"><Label>Address</Label><Input value={form.guardian_address} onChange={(e) => setForm({ ...form, guardian_address: e.target.value })} data-testid="guardian-address" /></div>
                                    <div className="space-y-1.5 md:col-span-2"><Label>Password * <span className="text-xs text-[#6B7280]">(at least 6 characters)</span></Label><Input type="password" value={form.guardian_password} onChange={(e) => setForm({ ...form, guardian_password: e.target.value })} required minLength={6} data-testid="guardian-password" /></div>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-sm font-semibold text-[#064E3B]">Children to register <span className="text-[#6B7280] font-normal">({n})</span></h3>
                                    <Button type="button" size="sm" variant="outline" onClick={addChild} data-testid="add-child-btn">
                                        <Plus className="h-3.5 w-3.5 mr-1" /> Add another child
                                    </Button>
                                </div>
                                <div className="space-y-4">
                                    {children.map((c, i) => (
                                        <div key={`child-${i}`} className="rounded-lg border border-[#E5E7EB] p-4 space-y-3" data-testid={`child-row-${i}`}>
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-medium text-[#6B7280]">Child #{i + 1}</span>
                                                {children.length > 1 && (
                                                    <Button type="button" size="sm" variant="ghost" className="text-[#EF4444] h-7 px-2" onClick={() => removeChild(i)} data-testid={`remove-child-${i}`}>
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <div className="space-y-1.5"><Label>First name *</Label><Input value={c.first_name} onChange={(e) => updateChild(i, { first_name: e.target.value })} required data-testid={`child-${i}-first-name`} /></div>
                                                <div className="space-y-1.5"><Label>Last name *</Label><Input value={c.last_name} onChange={(e) => updateChild(i, { last_name: e.target.value })} required data-testid={`child-${i}-last-name`} /></div>
                                                <div className="space-y-1.5"><Label>Date of birth *</Label><Input type="date" value={c.dob} onChange={(e) => updateChild(i, { dob: e.target.value })} required data-testid={`child-${i}-dob`} /></div>
                                                <div className="space-y-1.5">
                                                    <Label>Gender *</Label>
                                                    <Select value={c.gender} onValueChange={(v) => updateChild(i, { gender: v })}>
                                                        <SelectTrigger data-testid={`child-${i}-gender`}><SelectValue placeholder="Select" /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="male">Male</SelectItem>
                                                            <SelectItem value="female">Female</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-lg p-4">
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="mt-1"
                                        checked={form.join_waiting_list}
                                        onChange={(e) => setForm({ ...form, join_waiting_list: e.target.checked })}
                                        data-testid="join-waiting-list"
                                    />
                                    <span className="text-sm">
                                        <span className="font-medium text-[#9A3412]">Also add my {n === 1 ? 'child' : 'children'} to the main-program waiting list</span>
                                        <span className="block text-xs text-[#9A3412]/80 mt-1">
                                            Tick if you also want {n === 1 ? 'this child' : 'these children'} considered for Nurulhuda&rsquo;s main classes.
                                            Applies to all children listed above. Leave unticked if you only want to register for this single activity.
                                        </span>
                                    </span>
                                </label>
                            </div>

                            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-4 text-sm text-[#166534]">
                                <p className="font-medium" data-testid="total-due-line">
                                    You will be charged <span className="text-[#064E3B] font-bold">&euro;{totalDue.toFixed(2)}</span>
                                    {' '}({dueLabel}{n > 1 ? ` × ${n} children` : ''}) on the next page.
                                </p>
                                {deposit > 0 && deposit < cost && (
                                    <p className="text-xs mt-1">Remaining balance of &euro;{((cost - deposit) * n).toFixed(2)} (€{(cost - deposit).toFixed(2)} per child) will be due before the activity starts and can be paid from your guardian account.</p>
                                )}
                            </div>

                            <div className="flex justify-end gap-3 pt-2">
                                <Link to="/public/activities"><Button type="button" variant="outline">Cancel</Button></Link>
                                <Button type="submit" disabled={submitting} className="bg-[#064E3B] hover:bg-[#047857] text-white" data-testid="submit-public-register">
                                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    Continue to payment <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default PublicActivityRegister;
